import { ROSELoader } from './rose';

/**
 * @enum {Number}
 * @readonly
 */
export const PROPERTY_TYPE = {
  POSITION:           1,
  ROTATION:           2,
  SCALE:              3,
  AXIS_ROTATION:      4,
  BONE_INDEX:         5,
  DUMMY_INDEX:        6,
  PARENT:             7,
  ANIMATION:          8,
  COLLISION:          29,
  CONSTANT_ANIMATION: 30,
  VISIBLE_RANGE_SET:  31,
  USE_LIGHTMAP:       32
};

/**
 * @enum {Number}
 * @readonly
 */
export const COLLISION_MODE = {
  NONE: 0,
  BOUNDING_SPHERE: 1 << 0,
  AXIS_ALIGNED_BOUNDING_BOX: 1 << 1,
  POLYGON: 1 << 2,
  NOT_MOVEABLE: 1 << 3,
  NOT_PICKABLE: 1 << 4,
  HEIGHT_ONLY: 1 << 5,
  NO_CAMERA_COLLISION: 1 << 6
};

export class ModelMaterial {
  texturePath: string = '';
  forSkinning: boolean = false;
  alphaEnabled: boolean = false;
  twoSided: boolean = false;
  alphaTestEnabled: boolean = false;
  alphaRef: number = 0;
  depthTestEnabled: boolean = false;
  depthWriteEnabled: boolean = false;
  blendType: number = 0;
  useSpecular: boolean = false;
  alpha: number = 0;
  glowType: number = 0;
  glowColour: any = null;
}

export class ModelPart {
  // Static reference to enum for backwards compatibility
  static COLLISION_MODE = COLLISION_MODE;

  meshIdx: number = 0;
  materialIdx: number = 0;
  position?: any;
  rotation?: any;
  scale?: any;
  parent?: number;
  axisRotation?: any;
  collisionMode?: number;
  animPath?: string;
  visibleRangeSet?: number;
  useLightmap?: boolean;
  boneIndex?: number;
  dummyIndex?: number;
}

export class ModelEffect {
  effectIdx: number = 0;
  type: number = 0;
  position?: any;
  rotation?: any;
  scale?: any;
  parent?: number;
}

export class Model {
  // Static references to nested classes for backwards compatibility
  static Part = ModelPart;
  static Effect = ModelEffect;
  static Material = ModelMaterial;

  parts: ModelPart[] = [];
  effects: ModelEffect[] = [];
}

/**
 * @property {String[]} meshes
 * @property {ModelMaterial[]} materials
 * @property {String[]} effects
 * @property {Model[]} models
 */
export class ModelList {
  // Static references to enums for backwards compatibility
  static PROPERTY_TYPE = PROPERTY_TYPE;
  static COLLISION_MODE = COLLISION_MODE;
  // Static references to nested classes for backwards compatibility
  static Part = ModelPart;
  static Effect = ModelEffect;
  static Material = ModelMaterial;
  static Model = Model;

  meshes: string[] = [];
  materials: ModelMaterial[] = [];
  effects: string[] = [];
  models: (Model | null)[] = [];

  static load(path: any, callback: any) {
    ROSELoader.load(path, function(rh: any) {
      let i, j, meshes, materials, effects, models;
      const data = new ModelList();

      meshes = rh.readUint16();
      for (i = 0; i < meshes; ++i) {
        data.meshes.push(rh.readStr());
      }

      materials = rh.readUint16();
      for (i = 0; i < materials; ++i) {
        const material = new ModelMaterial();
        material.texturePath       = rh.readStr();
        material.forSkinning       = rh.readUint16() !== 0;
        material.alphaEnabled      = rh.readUint16() !== 0;
        material.twoSided          = rh.readUint16() !== 0;
        material.alphaTestEnabled  = rh.readUint16() !== 0;
        material.alphaRef          = rh.readUint16();
        material.depthTestEnabled  = rh.readUint16() !== 0;
        material.depthWriteEnabled = rh.readUint16() !== 0;
        material.blendType         = rh.readUint16();
        material.useSpecular       = rh.readUint16() !== 0;
        material.alpha             = rh.readFloat();
        material.glowType          = rh.readUint16();
        material.glowColour        = rh.readColour();
        data.materials.push(material);
      }

      effects = rh.readUint16();
      for (i = 0; i < effects; ++i) {
        data.effects.push(rh.readStr());
      }

      models = rh.readUint16();
      for (i = 0; i < models; ++i) {
        let model: Model | null, parts, type, size;

        rh.skip(3*4);
        parts = rh.readUint16();

        if (parts > 0) {
          model = new Model();

          for (j = 0; j < parts; ++j) {
            const part = new ModelPart();
            part.meshIdx = rh.readUint16();
            part.materialIdx = rh.readUint16();

            while ((type = rh.readUint8()) != 0) {
              size = rh.readUint8();

              switch (type) {
              case PROPERTY_TYPE.POSITION:
                part.position = rh.readVector3().multiplyScalar(ZZ_SCALE_IN);
                break;
              case PROPERTY_TYPE.ROTATION:
                part.rotation = rh.readQuatwxyz();
                break;
              case PROPERTY_TYPE.SCALE:
                part.scale = rh.readVector3();
                break;
              case PROPERTY_TYPE.PARENT:
                part.parent = rh.readUint16();
                break;
              case PROPERTY_TYPE.AXIS_ROTATION:
                part.axisRotation = rh.readQuatwxyz();
                break;
              case PROPERTY_TYPE.COLLISION:
                part.collisionMode = rh.readUint16();
                break;
              case PROPERTY_TYPE.CONSTANT_ANIMATION:
                part.animPath = rh.readStrLen(size);
                break;
              case PROPERTY_TYPE.VISIBLE_RANGE_SET:
                part.visibleRangeSet = rh.readUint16();
                break;
              case PROPERTY_TYPE.USE_LIGHTMAP:
                part.useLightmap = !!rh.readUint16();
                break;
              case PROPERTY_TYPE.BONE_INDEX:
                part.boneIndex = rh.readUint16();
                break;
              case PROPERTY_TYPE.DUMMY_INDEX:
                part.dummyIndex = rh.readUint16();
                break;
              default:
                console.log('Skipping unknown ZSC model part property type ' + type);
                rh.skip(size);
              }
            }

            model.parts.push(part);
          }

          effects = rh.readUint16();
          for (j = 0; j < effects; ++j) {
            const effect = new ModelEffect();
            effect.effectIdx = rh.readUint16();
            effect.type = rh.readUint16();

            while ((type = rh.readUint8()) != 0) {
              size = rh.readUint8();

              switch (type) {
              case PROPERTY_TYPE.POSITION:
                effect.position = rh.readVector3().multiplyScalar(ZZ_SCALE_IN);
                break;
              case PROPERTY_TYPE.ROTATION:
                effect.rotation = rh.readQuatwxyz();
                break;
              case PROPERTY_TYPE.SCALE:
                effect.scale = rh.readVector3();
                break;
              case PROPERTY_TYPE.PARENT:
                effect.parent = rh.readUint16();
                break;
              default:
                console.log('Skipping unknown ZSC model effect property type ' + type);
                rh.skip(size);
              }
            }

            model.effects.push(effect);
          }

          rh.skip(2 * 3 * 4);
        } else {
          model = null;
        }

        data.models.push(model);
      }

      callback(data);
    });
  }
}
